import { api, type BgAdmin } from '../sync/api';
import { thDateTime } from '../time';
import { $ } from './dom';

/*
 * Admin → ตั้งค่า → "ลบพื้นหลัง (เครื่องของกลุ่ม)" (0012): is the VM reachable (and why not), which model, how many
 * pictures per person per day, "วัดความเร็ว" per model on the real VM, and the latest requests with their reasons.
 */

const errText = (e: unknown) => e instanceof Error ? e.message : String(e);
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};
const STATUS_TH = { working: 'กำลังทำ', done: 'สำเร็จ', failed: 'ไม่สำเร็จ', lost: 'ไม่ได้คำตอบ' } as const;

export function initBgAdminCard() {
  const state = $('bgaState'), msg = $('bgaMsg'), limitIn = $('bgaLimit') as HTMLInputElement, saveBtn = $('bgaSave') as HTMLButtonElement;
  // ลบด้วย AI (0018): its own limit and its own speed test (model "lama")
  const eraseIn = $('bgaErase') as HTMLInputElement, eraseBench = $('bgaEraseBench') as HTMLButtonElement, eraseInfo = $('bgaEraseInfo');
  let info: BgAdmin | null = null;
  let picked = '';
  const bench = new Map<string, string>();   // model → last result (kept while the panel is open)
  let benching = '';

  const say = (box: HTMLElement, text: string, ok = false) => { box.textContent = text; box.hidden = !text; box.classList.toggle('ok', ok); };

  function paintModels() {
    const box = $('bgaModels');
    box.textContent = '';
    if (!info) return;
    const installed = new Map((info.health?.models ?? []).map(m => [m.id, m]));
    for (const m of info.models) {
      const row = el('label', 'profopt');
      const radio = el('input');
      radio.type = 'radio'; radio.name = 'bgaModel'; radio.value = m.id; radio.checked = m.id === picked;
      // a model the VM does not have would make every request fail: it cannot be picked (when the VM answered)
      radio.disabled = !!info.health && !info.health.models.some(x => x.id === m.id);
      radio.onchange = () => { picked = m.id; paintModels(); };
      const on = installed.get(m.id);
      const text = el('span');
      text.append(
        el('b', '', `${m.name}${m.id === info.model ? ' · ใช้อยู่' : ''}`),
        el('small', '', info.health ? (on ? (on.loaded ? 'ติดตั้งแล้ว · โหลดอยู่ในหน่วยความจำ' : 'ติดตั้งแล้ว · โหลดตอนใช้ครั้งแรก') : 'ยังไม่ได้ติดตั้งบนเครื่อง (BG_MODELS ใน .env)') : m.id)
      );
      const b = el('div', 'bgabench');
      const go = el('button', 'btn', benching === m.id ? 'กำลังวัด…' : 'วัดความเร็ว') as HTMLButtonElement;
      go.type = 'button';
      go.disabled = !!benching || !on;
      go.onclick = e => { e.preventDefault(); void runBench(m.id); };
      b.append(go, el('small', '', bench.get(m.id) ?? ''));
      text.append(b);
      row.append(radio, text);
      box.append(row);
    }
  }

  function paintJobs() {
    const list = $('bgaJobs');
    list.textContent = '';
    for (const j of info?.jobs ?? []) {
      const li = el('li', j.status === 'failed' || j.status === 'lost' ? 'bad' : '',
        `${thDateTime(new Date(j.at))} · ${j.by} · ${j.kind === 'erase' ? 'ลบด้วย AI' : j.model} · ${STATUS_TH[j.status]}${j.ms != null ? ` · ${(j.ms / 1000).toFixed(1)} วิ` : ''}${j.error ? ` — ${j.error}` : ''}`);
      list.append(li);
    }
  }

  async function load() {
    say(msg, '');
    say(state, 'กำลังตรวจเครื่องลบพื้นหลัง…', true);
    try {
      info = await api.admin.bg();
    } catch (e) { say(state, 'โหลดการตั้งค่าลบพื้นหลังไม่สำเร็จ: ' + errText(e)); return; }
    picked = info.model;
    limitIn.value = String(info.dailyLimit);
    const h = info.health;
    if (h) say(state, `เครื่องพร้อม · CPU ${h.cpus} ตัว · RAM ที่ใช้สูงสุด ${h.peakRamGb} GB · ${h.busy ? 'กำลังทำรูปอยู่' : 'ว่าง'}${h.waiting ? ` · รอคิว ${h.waiting}` : ''}`, true);
    else say(state, info.healthError || info.problem || 'ติดต่อเครื่องลบพื้นหลังไม่ได้');
    $('bgaInfo').textContent = `วันนี้ (${info.day}) ลบพื้นหลังสำเร็จ ${info.doneToday} รูป · ลบด้วย AI ${info.eraseDoneToday ?? 0} ครั้ง ทั้งกลุ่ม`;
    eraseIn.value = String(info.eraseLimit ?? '');
    const er = h?.erase;
    eraseInfo.textContent = !h ? '' : !er ? 'เครื่องยังเป็นรุ่นเก่า ไม่มีลบด้วย AI (build ใหม่ตาม README)'
      : er.installed ? (er.loaded ? 'ติดตั้งแล้ว · โหลดอยู่ในหน่วยความจำ' : 'ติดตั้งแล้ว · โหลดตอนใช้ครั้งแรก') : 'ยังไม่ได้ติดตั้งโมเดล LaMa บนเครื่อง (build ใหม่ตาม README หัวข้อ "ลบด้วย AI")';
    eraseBench.disabled = !er?.installed;
    paintModels(); paintJobs();
  }

  async function runBench(model: string) {
    benching = model; bench.set(model, ''); paintModels();
    try {
      const r = await api.admin.benchBg(model);
      bench.set(model, `${(r.ms / 1000).toFixed(1)} วินาที${r.includesLoading ? ' (รวมโหลดโมเดลครั้งแรก กดวัดซ้ำเพื่อดูเวลาจริง)' : ''} · RAM สูงสุด ${r.peakRamGb} GB`);
    } catch (e) {
      bench.set(model, 'วัดไม่สำเร็จ: ' + errText(e));
    } finally { benching = ''; paintModels(); }
  }

  eraseBench.onclick = async () => {
    eraseBench.disabled = true; eraseInfo.textContent = 'กำลังวัด…';
    try {
      const r = await api.admin.benchBg('lama');
      eraseInfo.textContent = `${(r.ms / 1000).toFixed(1)} วินาที${r.includesLoading ? ' (รวมโหลดโมเดลครั้งแรก กดวัดซ้ำเพื่อดูเวลาจริง)' : ''} · RAM สูงสุด ${r.peakRamGb} GB`;
    } catch (e) { eraseInfo.textContent = 'วัดไม่สำเร็จ: ' + errText(e); }
    finally { eraseBench.disabled = false; }
  };

  saveBtn.onclick = async () => {
    const raw = limitIn.value.trim(), n = Number(raw);
    if (!raw || !Number.isInteger(n) || n < 0 || n > (info?.limitMax ?? 1000)) { say(msg, `จำนวนต่อวันต้องเป็นจำนวนเต็ม 0 ถึง ${info?.limitMax ?? 1000} ยังไม่ได้บันทึก`); return; }
    if (!picked) { say(msg, 'เลือกโมเดลก่อน ยังไม่ได้บันทึก'); return; }
    if (info?.health && !info.health.models.some(m => m.id === picked)) { say(msg, `เครื่องลบพื้นหลังไม่ได้ติดตั้งโมเดล ${picked} (เพิ่มใน BG_MODELS แล้ว build ใหม่) ยังไม่ได้บันทึก`); return; }
    const eraw = eraseIn.value.trim(), en = Number(eraw);
    if (!eraw || !Number.isInteger(en) || en < 0 || en > (info?.limitMax ?? 1000)) { say(msg, `จำนวนลบด้วย AI ต่อวันต้องเป็นจำนวนเต็ม 0 ถึง ${info?.limitMax ?? 1000} ยังไม่ได้บันทึก`); return; }
    saveBtn.disabled = true;
    try {
      const r = await api.admin.saveBg(n, picked, en);
      if (r.eraseLimit === undefined) { say(msg, 'บันทึกลบพื้นหลังแล้ว แต่เซิร์ฟเวอร์ยังเป็นรุ่นเก่า จำนวนลบด้วย AI ยังไม่ถูกบันทึก (รอ deploy แล้วลองใหม่)'); return; }
      say(msg, `บันทึกแล้ว: ${r.dailyLimit === 0 ? 'ปิดการลบพื้นหลัง' : `คนละ ${r.dailyLimit} รูปต่อวัน`} · โมเดล ${r.model} · ลบด้วย AI ${r.eraseLimit === 0 ? 'ปิด' : `คนละ ${r.eraseLimit} ครั้งต่อวัน`}`, true);
      if (info) { info.dailyLimit = r.dailyLimit; info.model = r.model; info.eraseLimit = r.eraseLimit; paintModels(); }
    } catch (e) { say(msg, 'บันทึกไม่สำเร็จ: ' + errText(e)); }
    finally { saveBtn.disabled = false; }
  };

  return { load };
}
